import {
  createApiFactory,
  createComponentExtension,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  ScorecardLeaderboardClient,
  scorecardLeaderboardApiRef,
} from './api/ScorecardLeaderboardClient';
import { rootRouteRef } from './routes';

/**
 * Scorecard — renders precomputed Tech Insights facts as a Group entity tab,
 * an Overview-page summary card, and one routable page: the cross-team
 * `ScorecardLeaderboardPage` at `/scorecard-leaderboard` (PAT-900, gated by
 * `scorecard-v2` and linked from the Preview page). The leaderboard reads
 * every Group's facts in one call to the backend's
 * `GET /api/tech-insights/scorecard-leaderboard`, via
 * `scorecardLeaderboardApiRef`.
 *
 * Hosts three scorecards, one per Tech Insights fact, all surfaced through a
 * single Tab (`EntityScorecardsTab`, at `/scorecards`) and a single Overview
 * Card (`ScorecardsSummaryCard`):
 *  - `team-lint-rollup` (Portfolio linter) — `useTeamLintRollup`
 *  - `catalog-hygiene` (Catalog Hygiene) — `useCatalogHygieneRollup`
 *  - `team-profile-completeness` (Team Profile) — `useTeamProfileCompleteness`
 *
 * Originally three separate plugins (`team-health`, `catalog-hygiene`,
 * `team-profile-completeness`), then three Tab/Card pairs within this one
 * package, each with no backend, no DB, and no routes of its own. Consolidated
 * again into one Tab + one Card because the three had near-identical
 * presentation shells; adding a fourth scorecard now means adding a section
 * component (see `components/EntityScorecardsTab/`) + a fact-parsing hook, not
 * a new Tab/Card pair. `useScorecardFact` in `./hooks/useScorecardFact` is the
 * shared fetch/loading/error logic every scorecard hook wraps, and
 * `useScorecardsSummary` composes all three into the one average the summary
 * card and the tab's header both show.
 *
 * Both `EntityScorecardsTab` and `ScorecardsSummaryCard` are shelled on
 * `EntityInfoCard` (`@backstage/plugin-catalog-react`, built on
 * `@backstage/ui`) — matching upstream cards like `AboutCard`/`OwnershipCard`
 * — rather than the classic `InfoCard` every other card in this repo still
 * uses. This was reached by building a second, `EntityInfoCard`-based set of
 * components (`*V2`) behind the `scorecard-v2` feature flag, rendering side
 * by side with the original `InfoCard`-based set on the Overview page and at
 * `/scorecards` vs `/scorecards-v2`, and keeping the winner once compared —
 * the `InfoCard` set has since been deleted and the `*V2` suffix dropped from
 * the survivors. `scorecard-v2` is still declared here and still gates these
 * components — deliberately not yet made generally available while this
 * settles — so no code should assume the flag is unused deadweight.
 *
 * `EntityComponentScorecardsCard` is the Component-page counterpart
 * (PAT-899): one `EntityInfoCard` with a Health section (the per-Component
 * `portfolio-linter` fact) and a Catalog Hygiene section (computed in the
 * browser via `classifyHygiene` from `scorecard-common`). It replaces the
 * generic `EntityTechInsightsScorecardCard` when `scorecard-v2` is on.
 */
export const scorecardPlugin = createPlugin({
  id: 'scorecard',
  featureFlags: [{ name: 'scorecard-v2' }],
  routes: {
    root: rootRouteRef,
  },
  apis: [
    createApiFactory({
      api: scorecardLeaderboardApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: deps => new ScorecardLeaderboardClient(deps),
    }),
  ],
});

export const ScorecardLeaderboardPage = scorecardPlugin.provide(
  createRoutableExtension({
    name: 'ScorecardLeaderboardPage',
    component: () =>
      import('./components/ScorecardLeaderboardPage/ScorecardLeaderboardPage').then(
        m => m.ScorecardLeaderboardPage,
      ),
    mountPoint: rootRouteRef,
  }),
);

export const EntityScorecardsTab = scorecardPlugin.provide(
  createComponentExtension({
    name: 'EntityScorecardsTab',
    component: {
      lazy: () =>
        import('./components/EntityScorecardsTab/EntityScorecardsTab').then(
          m => m.EntityScorecardsTab,
        ),
    },
  }),
);

export const ScorecardsSummaryCard = scorecardPlugin.provide(
  createComponentExtension({
    name: 'ScorecardsSummaryCard',
    component: {
      lazy: () =>
        import('./components/ScorecardsSummaryCard/ScorecardsSummaryCard').then(
          m => m.ScorecardsSummaryCard,
        ),
    },
  }),
);

export const EntityComponentScorecardsCard = scorecardPlugin.provide(
  createComponentExtension({
    name: 'EntityComponentScorecardsCard',
    component: {
      lazy: () =>
        import('./components/EntityComponentScorecardsCard/EntityComponentScorecardsCard').then(
          m => m.EntityComponentScorecardsCard,
        ),
    },
  }),
);
